import React, { useMemo } from 'react';
import {
  Page,
  LayoutSingleColumn,
  NamedLink,
  PrimaryButton,
  SecondaryButton,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import founderPhoto from '../../assets/fondateur.png';
import heroImage from '../../assets/about-smart-home.png';
import SkeepersCarouselReviews from '../../components/SkeepersCarouselReviews/SkeepersCarouselReviews';
import { useIntl } from '../../util/reactIntl';
import css from './AboutPage.module.css';

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

const HeadsetIcon = props => (
  <svg {...iconProps} {...props}>
    <path d="M3 12a9 9 0 0 1 18 0" />
    <path d="M21 16v2a3 3 0 0 1-3 3h-3" />
    <rect x="2" y="12" width="5" height="7" rx="1.5" />
    <rect x="17" y="12" width="5" height="7" rx="1.5" />
  </svg>
);

const ShieldCheckIcon = props => (
  <svg {...iconProps} {...props}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const AwardIcon = props => (
  <svg {...iconProps} {...props}>
    <circle cx="12" cy="9" r="6" />
    <path d="m8.5 13.5-1.8 7.5L12 18l5.3 3-1.8-7.5" />
  </svg>
);

const TruckIcon = props => (
  <svg {...iconProps} {...props}>
    <path d="M3 6h11v11H3z" />
    <path d="M14 9h4l3 3v5h-7" />
    <circle cx="7" cy="18" r="2" />
    <circle cx="17" cy="18" r="2" />
  </svg>
);

const PRINCIPLE_IDS = ['AboutPage.principle1', 'AboutPage.principle2', 'AboutPage.principle3'];

const TRUST_DEFS = [
  { Icon: HeadsetIcon, titleId: 'AboutPage.trust1Title', textId: 'AboutPage.trust1Text' },
  { Icon: ShieldCheckIcon, titleId: 'AboutPage.trust2Title', textId: 'AboutPage.trust2Text' },
  { Icon: AwardIcon, titleId: 'AboutPage.trust3Title', textId: 'AboutPage.trust3Text' },
  { Icon: TruckIcon, titleId: 'AboutPage.trust4Title', textId: 'AboutPage.trust4Text' },
];

const AboutPage = () => {
  const intl = useIntl();

  const principles = useMemo(
    () => PRINCIPLE_IDS.map(id => intl.formatMessage({ id })),
    [intl]
  );

  const trustPillars = useMemo(
    () =>
      TRUST_DEFS.map(({ Icon, titleId, textId }) => ({
        Icon,
        title: intl.formatMessage({ id: titleId }),
        text: intl.formatMessage({ id: textId }),
      })),
    [intl]
  );

  return (
    <Page
      title={intl.formatMessage({ id: 'AboutPage.metaTitle' })}
      description={intl.formatMessage({ id: 'AboutPage.metaDescription' })}
      scrollingDisabled={false}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <section className={css.companySection} aria-labelledby="about-company-heading">
            <div className={css.container}>
              <div className={css.companyGrid}>
                <div className={css.companyText}>
                  <h1 id="about-company-heading" className={css.companyTitle}>
                    {intl.formatMessage({ id: 'AboutPage.title' })}
                  </h1>
                  <p className={css.companySubtitle}>
                    {intl.formatMessage({ id: 'AboutPage.subtitle' })}
                  </p>
                  <p className={css.companyLead}>{intl.formatMessage({ id: 'AboutPage.lead' })}</p>
                  <p className={css.companyDescription}>
                    {intl.formatMessage({ id: 'AboutPage.body' })}
                  </p>
                  <ul className={css.companyPrinciples}>
                    {principles.map((line, index) => (
                      <li key={PRINCIPLE_IDS[index]} className={css.companyPrincipleItem}>
                        {line}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={css.companyMedia}>
                  <img
                    src={heroImage}
                    alt={intl.formatMessage({ id: 'AboutPage.heroImageAlt' })}
                    className={css.companyMediaImage}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className={css.founderSection} aria-labelledby="about-founder-heading">
            <div className={css.container}>
              <h2 id="about-founder-heading" className={css.sectionTitle}>
                {intl.formatMessage({ id: 'AboutPage.founderHeading' })}
              </h2>
              <p className={css.sectionDescription}>
                {intl.formatMessage({ id: 'AboutPage.founderIntro' })}
              </p>
              <div className={css.founderCard}>
                <div className={css.founderPortrait}>
                  <img
                    src={founderPhoto}
                    alt={intl.formatMessage({ id: 'AboutPage.founderImageAlt' })}
                    width={200}
                    height={200}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className={css.founderContent}>
                  <h3 className={css.founderName}>
                    {intl.formatMessage({ id: 'AboutPage.founderName' })}
                  </h3>
                  <p className={css.founderRole}>
                    {intl.formatMessage({ id: 'AboutPage.founderRole' })}
                  </p>
                  <p className={css.founderBio}>
                    {intl.formatMessage({ id: 'AboutPage.founderBio1' })}
                  </p>
                  <p className={css.founderBio}>
                    {intl.formatMessage({ id: 'AboutPage.founderBio2' })}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className={css.trustSection} aria-labelledby="about-trust-heading">
            <div className={css.container}>
              <SkeepersCarouselReviews
                className={css.verifiedReviewsCarousel}
                titleClassName={css.sectionTitle}
              />
              <h2 id="about-trust-heading" className={css.sectionTitle}>
                {intl.formatMessage({ id: 'AboutPage.trustHeading' })}
              </h2>
              <p className={css.sectionDescription}>
                {intl.formatMessage({ id: 'AboutPage.trustIntro' })}
              </p>
              <ul className={css.trustGrid}>
                {trustPillars.map((item, index) => {
                  const { Icon } = item;
                  return (
                    <li key={TRUST_DEFS[index].titleId} className={css.trustCard}>
                      <span className={css.trustIcon} aria-hidden="true">
                        <Icon />
                      </span>
                      <h3 className={css.trustTitle}>{item.title}</h3>
                      <p className={css.trustText}>{item.text}</p>
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>

          <section className={css.contactCtaSection}>
            <div className={css.contactCtaInner}>
              <h2 className={css.contactCtaTitle}>
                {intl.formatMessage({ id: 'AboutPage.ctaTitle' })}
              </h2>
              <p className={css.contactCtaDescription}>
                {intl.formatMessage({ id: 'AboutPage.ctaDescription' })}
              </p>
              <div className={css.contactCtaButtons}>
                <NamedLink name="ContactPage" className={css.contactCtaButtonWrap}>
                  <PrimaryButton>
                    {intl.formatMessage({ id: 'AboutPage.ctaContact' })}
                  </PrimaryButton>
                </NamedLink>
                <NamedLink name="SearchPage" className={css.contactCtaButtonWrap}>
                  <SecondaryButton className={css.contactCtaButtonSecondary}>
                    {intl.formatMessage({ id: 'AboutPage.ctaBrowse' })}
                  </SecondaryButton>
                </NamedLink>
              </div>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default AboutPage;
